import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card, Table, Spinner } from "react-bootstrap";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../services/firebase";
import StatCard from "../../components/common/StatCard";
import SubscriptionBadge from "../../components/common/SubscriptionBadge";
import dayjs from "dayjs";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";

const withinDays = (date, days) => {
  if (!date) return false;
  const d = dayjs(date);
  return d.isAfter(dayjs()) && d.diff(dayjs(), "day") <= days;
};

const isActive = (sub) => sub?.status === "active" && dayjs(sub?.renewalDate).isAfter(dayjs());
const isExpired = (sub) => !sub || sub?.status === "expired" || dayjs(sub?.renewalDate).isBefore(dayjs());

const normalizeDoc = (doc) => {
  const data = doc.data();
  // support Firestore Timestamp or ISO strings
  const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : null);
  const renewalDate = data.subscription?.renewalDate?.toDate ? data.subscription.renewalDate.toDate() : (data.subscription?.renewalDate ? new Date(data.subscription.renewalDate) : null);
  return {
    id: doc.id,
    name: data.name || "—",
    strength: Number(data.strength || 0),
    plan: (data.plan || "free").toLowerCase(),
    subscription: data.subscription ? { ...data.subscription, renewalDate } : null,
    createdAt
  };
};

const Dashboard = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  // live updates
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "schools"), (snap) => {
      const rows = snap.docs.map(normalizeDoc);
      setSchools(rows);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, []);

  // aggregates
  const {
    totalSchools,
    totalStrength,
    activeSubs,
    expiringSoon,
    expiredSubs,
    byPlanData,
    registrations7d
  } = useMemo(() => {
    const totalSchools = schools.length;
    const totalStrength = schools.reduce((s, x) => s + (x.strength || 0), 0);

    let activeSubs = 0, expiringSoon = 0, expiredSubs = 0;
    const planCount = {};

    schools.forEach((s) => {
      const st = s.subscription?.status || "expired";
      planCount[s.plan] = (planCount[s.plan] || 0) + 1;

      if (isActive(s.subscription)) {
        activeSubs += 1;
        if (withinDays(s.subscription?.renewalDate, 14)) expiringSoon += 1;
      } else {
        if (isExpired(s.subscription)) expiredSubs += 1;
      }
    });

    const byPlanData = Object.entries(planCount).map(([plan, count]) => ({ plan, count }));

    // registrations last 7 days
    const days = [...Array(7)].map((_, i) => {
      const d = dayjs().subtract(6 - i, "day").startOf("day");
      return { key: d.format("YYYY-MM-DD"), label: d.format("DD MMM"), count: 0 };
    });
    schools.forEach((s) => {
      if (!s.createdAt) return;
      const key = dayjs(s.createdAt).startOf("day").format("YYYY-MM-DD");
      const bucket = days.find((d) => d.key === key);
      if (bucket) bucket.count += 1;
    });

    return {
      totalSchools,
      totalStrength,
      activeSubs,
      expiringSoon,
      expiredSubs,
      byPlanData,
      registrations7d: days
    };
  }, [schools]);

  return (
    <Container fluid className="p-3">
      <Row className="g-3">
        <Col md={3}><StatCard title="Schools Registered" value={loading ? "…" : totalSchools} sub="Total organizations on platform" iconClass="bi-buildings" /></Col>
        <Col md={3}><StatCard title="Total Strength" value={loading ? "…" : totalStrength.toLocaleString()} sub="Students across all schools" iconClass="bi-people-fill" /></Col>
        <Col md={3}><StatCard title="Active Subscriptions" value={loading ? "…" : activeSubs} sub={`${expiringSoon} expiring in 14 days`} iconClass="bi-bag-check" /></Col>
        <Col md={3}><StatCard title="Expired/Lapsed" value={loading ? "…" : expiredSubs} sub="Needs attention" iconClass="bi-exclamation-triangle" /></Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col lg={6}>
          <Card className="p-3 h-100">
            <h6 className="mb-3">Subscriptions by Plan</h6>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={byPlanData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="plan" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="p-3 h-100">
            <h6 className="mb-3">New Schools (Last 7 Days)</h6>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={registrations7d}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col>
          <Card className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="m-0">Subscription Details</h6>
              <small className="text-muted">{loading ? "Loading…" : `${schools.length} schools`}</small>
            </div>

            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead>
                  <tr>
                    <th>School</th>
                    <th className="text-center">Strength</th>
                    <th className="text-center">Plan</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Renewal</th>
                    <th className="text-center">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="text-center py-5"><Spinner animation="border" size="sm" /> Loading…</td></tr>
                  ) : schools.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-4 text-muted">No schools yet</td></tr>
                  ) : (
                    schools
                      .sort((a,b) => (b.strength||0) - (a.strength||0))
                      .map((s) => (
                        <tr key={s.id}>
                          <td>
                            <div className="fw-semibold">{s.name}</div>
                            <div className="text-muted small">{s.id}</div>
                          </td>
                          <td className="text-center">{(s.strength || 0).toLocaleString()}</td>
                          <td className="text-center text-capitalize">{s.plan}</td>
                          <td className="text-center">
                            <SubscriptionBadge status={s.subscription?.status || "expired"} />
                            {withinDays(s.subscription?.renewalDate, 14) && (
                              <div className="small text-warning">expiring soon</div>
                            )}
                          </td>
                          <td className="text-center">
                            {s.subscription?.renewalDate
                              ? dayjs(s.subscription.renewalDate).format("DD MMM YYYY")
                              : "—"}
                          </td>
                          <td className="text-center">{s.createdAt ? dayjs(s.createdAt).format("DD MMM YYYY") : "—"}</td>
                        </tr>
                      ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
